import { IHotelStaticInfo } from '@core/models/HotelStaticInfo/hotelStaticInfo.interface';
import { IHotelAvailability } from '@core/models/HotelAvailability/IHotelAvailability';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  MdToastService,
  PriceService,
  ReqService,
  ScrollService,
  SearchStateService,
} from '@core/services';
import { UsersViewsComponent } from '../users-views/users-views.component';
import { Observable, of, Subject } from 'rxjs';
import { environment } from '@env';
import { Store } from '@ngrx/store';
import { AppState } from '@store/app.reducer';
import { filter, takeUntil } from 'rxjs/operators';
import { selectHotelAvailability } from '@store/hotel-availability/hotel-availability.selectors';
import {
  setPageSize,
  showMoreRooms,
} from '@store/hotel-availability/hotelAvailability.actions';
import { MbButton } from '@am/moebius';
import { RelatedHotelsComponent } from '@commons/related-hotels/related-hotels.component';
import { TripadavisorReviewsComponent } from '../tripadvisor-reviews/tripadvisor-reviews.component';
import { ServicesComponent } from '../services/services.component';
import { AccommodationFeeComponent } from '../accommodation-fee/accommodation-fee.component';
import { RoomOptionsComponent } from '../room-options/room-options/room-options.component';
import { LoaderDetailComponent } from '@commons/loader-detail/loader-detail.component';
import { FiltersContainerComponent } from './filters-container/filters-container.component';
import { PriceBoxComponent } from '../price-box/price-box.component';
import { CommentsComponent } from '../comments/comments.component';
import { CancelPoliciesComponent } from '../cancel-policies/cancel-policies.component';
import { CheckTimesComponent } from '../check-times/check-times.component';
import { ParkingDisneyComponent } from '../../disney/parking/parking.component';
import { ActivitiesInformationDisneyComponent } from '../../disney/activities-information/activities-information.component';
import { DescriptionComponent } from '../description/description.component';
import { AmenitiesComponent } from '@commons/amenities/amenities.component';
import { AvuxiComponent } from '@commons/avuxi/avuxi.component';
import { DisneyBenefitsComponent } from '../../disney/disney-benefits/disney-benefits.component';
import { UniversalBenefitsDetailComponent } from '../universal-benefits-detail/universal-benefits-detail.component';
import { ImageMessageComponent } from '../image-message/image-message.component';
import { HeaderDetailComponent } from '../header-detail/header-detail.component';
import { BreadCrumbComponent } from '@commons/breadcrumb/breadcrumb.component';
import { SearchWidgetComponent } from '@commons/search-widget/search-widget.component';
import { NgClass, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';

@Component({
    selector: 'hotel-detail',
    templateUrl: './hotel-detail.component.html',
    styleUrls: ['./hotel-detail.component.scss'],
    standalone: true,
    imports: [
        NgClass,
        NgIf,
        SearchWidgetComponent,
        BreadCrumbComponent,
        HeaderDetailComponent,
        ImageMessageComponent,
        UniversalBenefitsDetailComponent,
        DisneyBenefitsComponent,
        AvuxiComponent,
        AmenitiesComponent,
        DescriptionComponent,
        ActivitiesInformationDisneyComponent,
        ParkingDisneyComponent,
        CheckTimesComponent,
        CancelPoliciesComponent,
        CommentsComponent,
        PriceBoxComponent,
        FiltersContainerComponent,
        LoaderDetailComponent,
        RoomOptionsComponent,
        AccommodationFeeComponent,
        ServicesComponent,
        TripadavisorReviewsComponent,
        RelatedHotelsComponent,
        NgSwitch,
        NgSwitchCase,
        MbButton,
    ],
})
export class HotelDetailComponent implements AfterViewInit, OnInit, OnChanges, OnDestroy {
  public readonly ROOMS_PAGE_SIZE = 4;

  @Input()
  isLanding: boolean;
  @Input()
  searchedEntity: any;

  @Output()
  resetState: EventEmitter<'USD' | 'ARS' | undefined> = new EventEmitter();

  @Input()
  busy: boolean;
  @Input()
  accommodationFee: any;
  @Input()
  recommendedHotels: any;

  @Input() set error(error: any) {
    this.errorResponse = error;
  }

  @ViewChild('expertsCarousel') expertsCarousel: Component;

  public isHomestay = false;
  public locale: string;
  public country: string;
  public priceByValue: string;
  public hasUniversal: boolean;
  public reviewsExperts: Observable<Component>;
  public hotelPosition: string;
  public showAvuxi: boolean;
  public isDisney = false;
  public brand: string;
  public isCvc: boolean;
  public bestAvailable: any;
  public showRefundableRateAlert: boolean;
  public selectedPaymentMethod: any;
  public showPaymentsOptionsAlert: any;
  public rooms: HTMLElement;
  public reviews: HTMLElement;
  public related: HTMLElement;
  public scrollMenu = false;
  public actualContainer = 'description';
  public comments: string;
  public hotelStaticInfo: IHotelStaticInfo;
  public hotelAvailabilityInfo: IHotelAvailability;
  public isExperts = false;
  public errorResponse: any;
  public showRoomOptions = false;
  private refundableRateSelected: boolean;
  private disableScroll = false;
  private destroy$: Subject<void> = new Subject();

  @HostListener('window:scroll')
  scroll() {
    this.onScroll();
  }

  constructor(
    private searchStateService: SearchStateService,
    private priceService: PriceService,
    private reqService: ReqService,
    private mdToastService: MdToastService,
    private viewContainerRef: ViewContainerRef,
    private scrollService: ScrollService,
    private store: Store<AppState>,
  ) {}

  ngOnInit() {
    this.isCvc = this.reqService.isCVC();
    this.brand = this.reqService.getBrand();
    this.locale = this.reqService.getLocale();
    this.country = this.reqService.getCountry();
    this.hotelStaticInfo = this.reqService.getHotelStaticInfo();
    this.isDisney = this.reqService.isDisneyHotel();
    this.hotelPosition = `${this.hotelStaticInfo.hotel_position.latitude},${this.hotelStaticInfo.hotel_position.longitude}`;

    this.priceByValue = this.searchStateService.getPriceBy();
    this.isExperts = this.reqService.isExperts();
    this.showAvuxi =
      (!this.reqService.isBot() && environment.production) ||
      this.searchStateService.getLocationSearch().poi;
    this.onScroll();
    this.selectedPaymentMethod = this.searchStateService.getPaymentMethod();

    this.store
      .select(selectHotelAvailability)
      .pipe(
        filter((hotelAvailability) => hotelAvailability !== null),
        takeUntil(this.destroy$),
      )
      .subscribe((hotelAvailability: IHotelAvailability) => {
        if (hotelAvailability !== null) {
          this.hotelAvailabilityInfo = hotelAvailability;
          this.isHomestay = this.hotelAvailabilityInfo.isHomestay;

          this.comments = this.hotelAvailabilityInfo.comments
            ? this.hotelAvailabilityInfo.comments
            : null;

          if (!this.hasUniversal && this.hotelAvailabilityInfo) {
            this.hasUniversal = this.hotelAvailabilityInfo.hotel_chain === 'UNIVERSAL';
          }
          this.bestAvailable = this.getRate(true);
        }
        // ToDo: move this validaton to store or service
        this.showRoomOptions =
          (hotelAvailability.prepaid_room_clusters &&
            hotelAvailability.prepaid_room_clusters.length > 0) ||
          (hotelAvailability.paid_at_destination_room_clusters &&
            hotelAvailability.paid_at_destination_room_clusters.length > 0);
        this.isLanding = false;
      });

    this.store.dispatch(setPageSize({ pageSize: this.ROOMS_PAGE_SIZE }));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.hotelAvailabilityInfo && !changes.hotelAvailabilityInfo.isFirstChange()) {
      this.isHomestay = this.hotelAvailabilityInfo.isHomestay;
      this.comments = this.hotelAvailabilityInfo.comments
        ? this.hotelAvailabilityInfo.comments
        : null;
    }

    if (!this.hasUniversal && this.hotelAvailabilityInfo) {
      this.hasUniversal = this.hotelAvailabilityInfo.hotel_chain === 'UNIVERSAL';
    }
  }

  ngAfterViewInit(): void {
    Promise.resolve(null).then(() => (this.reviewsExperts = of(this.expertsCarousel)));
  }

  /**
   * Detect scroll changes for selected tabs on menu
   */
  private onScroll() {
    if (!this.disableScroll) {
      const containers = ['description', 'rooms', 'reviews', 'related'];
      containers.forEach((container) => {
        this[container] = document.querySelector(`#${container}-container`);
        if (this[container]) {
          if (this[container].offsetTop - 80 <= window.scrollY) {
            this.actualContainer = container;
          }
        }
      });
    }
    this.scrollMenu = window.scrollY > 340;
  }

  /**
   * Handles menu option selection
   * @param data - option selected
   */
  public handleClickMenuOption(data: string) {
    this.disableScroll = true;
    this.scrollService.scrollToElement(
      `#${this.actualContainer}-container`,
      `#${data}-container`,
      undefined,
      8,
      () => {
        this.disableScroll = false;
      },
    );
    this.actualContainer = data;
  }

  public priceByChange(data) {
    this.searchStateService.setPriceBy(data);
    this.priceByValue = data;
  }

  public onResetState(currencyCode?: 'USD' | 'ARS') {
    this.resetState.emit(currencyCode);
  }

  /**
   * Toggles tabs to show the different payment method
   */
  public paymentMethodSelected(data) {
    this.selectedPaymentMethod = data;
    this.bestAvailable = this.getRate();
  }

  /**
   * Toggles tabs to show the different room by refundable rates
   */
  public handleRefundableRateChange(data) {
    this.refundableRateSelected = data;
    this.bestAvailable = this.getRate();
  }

  public showMoreRooms() {
    this.store.dispatch(showMoreRooms());
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Toggles refundable rates alert
   */
  private checkRefundableRateVisibility() {
    const allRooms = this.hotelAvailabilityInfo.prepaid_room_clusters.concat(
      this.hotelAvailabilityInfo.paid_at_destination_room_clusters,
    );
    let refundable = false;
    let nonrefundable = false;
    allRooms.forEach((room) => {
      if (room.rates.some((rate) => rate.refundable)) {
        refundable = true;
      }
      if (room.rates.some((rate) => !rate.refundable)) {
        nonrefundable = true;
      }
    });
    return refundable && nonrefundable;
  }

  private shouldShowRefundableRateAlert(shouldShowPaymentsOptionsAlert) {
    const refundableRateSelected = this.searchStateService.getFlexible();
    const showRefundableRateSwitch =
      !this.isDisney && this.checkRefundableRateVisibility();

    return (this.showRefundableRateAlert =
      refundableRateSelected &&
      showRefundableRateSwitch &&
      !shouldShowPaymentsOptionsAlert);
  }

  /**
   * Toggles Payment Methods alert
   */
  private shouldShowPaymentsOptionsAlert() {
    if (this.bestAvailable) {
      return (this.showPaymentsOptionsAlert =
        this.priceService.shouldShowPaymentsOptionsAlert(
          this.bestAvailable,
          this.priceByValue,
          this.hotelAvailabilityInfo,
        ));
    }
  }

  private getRate(displayAlerts?) {
    const paymentMethod = this.selectedPaymentMethod
      ? this.selectedPaymentMethod
      : this.searchStateService.getPaymentMethod();

    if (this.hotelAvailabilityInfo === undefined) {
      return {};
    }
    this.hotelAvailabilityInfo.refundable_prepaid_room_clusters =
      this.priceService.filterRefundableRooms(
        this.hotelAvailabilityInfo.prepaid_room_clusters,
        this.isDisney,
      );
    this.hotelAvailabilityInfo.refundable_paid_at_destination_room_clusters =
      this.priceService.filterRefundableRooms(
        this.hotelAvailabilityInfo.paid_at_destination_room_clusters,
        this.isDisney,
      );
    this.hotelAvailabilityInfo.all_refundable_room_clusters =
      this.hotelAvailabilityInfo.refundable_prepaid_room_clusters.concat(
        this.hotelAvailabilityInfo.refundable_paid_at_destination_room_clusters,
      );

    const rate = this.isDisney
      ? this.hotelAvailabilityInfo.best_disney_room_cluster
      : this.priceService.getRate(
          this.hotelAvailabilityInfo,
          paymentMethod,
          this.refundableRateSelected,
        );

    if (displayAlerts && rate !== undefined) {
      this.showHotelDetailAlerts();
    }
    return rate;
  }

  private showHotelDetailAlerts() {
    this.mdToastService.setRootViewContainerRef(this.viewContainerRef);
    setTimeout(() => {
      const shouldShowPaymentsOptionsAlert = this.shouldShowPaymentsOptionsAlert();
      const shouldShowRefundableRateAlert = this.shouldShowRefundableRateAlert(
        shouldShowPaymentsOptionsAlert,
      );
      if (!shouldShowRefundableRateAlert && !shouldShowPaymentsOptionsAlert) {
        const acceleratorData = {
          data: {
            nameHotel: this.hotelStaticInfo.name,
            usersViews: this.hotelStaticInfo.users_views,
          },
          hideDelay: 5000,
        };
        this.mdToastService.show(UsersViewsComponent, acceleratorData);
      }
    }, 2000);
  }
}
